/**
 * Fantasy Console 0 - Toast Notification Context
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { Toast, ToastType } from '../types'

interface ToastContextType {
  toasts: Toast[]
  showToast: (message: string, type?: ToastType, duration?: number) => void
  dismissToast: (id: string) => void
  success: (message: string) => void
  error: (message: string) => void
  warning: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastContextType | null>(null)

const DEFAULT_DURATION = 4000
const MAX_TOASTS = 5

let toastIdCounter = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      duration: number = DEFAULT_DURATION,
    ) => {
      const id = `toast-${++toastIdCounter}`

      const newToast: Toast = {
        id,
        message,
        type,
        duration,
      }

      setToasts((prev) => {
        const updated = [...prev, newToast]
        if (updated.length > MAX_TOASTS) {
          return updated.slice(-MAX_TOASTS)
        }
        return updated
      })

      if (duration > 0) {
        setTimeout(() => {
          dismissToast(id)
        }, duration)
      }
    },
    [dismissToast],
  )

  const success = useCallback(
    (message: string) => showToast(message, 'success'),
    [showToast],
  )

  const error = useCallback(
    (message: string) => showToast(message, 'error', 6000),
    [showToast],
  )

  const warning = useCallback(
    (message: string) => showToast(message, 'warning'),
    [showToast],
  )

  const info = useCallback(
    (message: string) => showToast(message, 'info'),
    [showToast],
  )

  return (
    <ToastContext.Provider
      value={{
        toasts,
        showToast,
        dismissToast,
        success,
        error,
        warning,
        info,
      }}
    >
      {children}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextType {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
